/**
 * CompletionStep — celebratory close. Three explicit next actions, a small
 * "what's next" footer, and a confetti-style cyan ripple over the brand
 * mark to mark completion. No marketing fluff — the inspector is here to
 * work, so we point at the three things they can do right now.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Map,
  ClipboardCheck,
  Plus,
  CheckCircle2,
  ArrowRight,
  HelpCircle,
} from 'lucide-react';
import StepShell from './StepShell';
import { BrandMark } from '../../../components/brand/BrandLogo';
import { StepId, OnboardingProfile } from '../types';

interface Props {
  onBack: () => void;
  onComplete: () => void;
  profile: OnboardingProfile;
}

export default function CompletionStep({ onBack, onComplete, profile }: Props) {
  const navigate = useNavigate();

  const goAndComplete = (path: string) => {
    onComplete();
    navigate(path);
  };

  return (
    <StepShell
      step={'completion' as StepId}
      onBack={onBack}
      hideNav
    >
      {/* Hero */}
      <div className="relative rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-slate-900 via-slate-900 to-slate-950 p-8 md:p-12 overflow-hidden"
           style={{ boxShadow: '0 0 60px rgba(16,185,129,0.18)' }}>
        {/* Ripple ring behind the mark */}
        <div className="absolute -top-8 -left-8 w-48 h-48 rounded-full bg-cyan-500/5 blur-3xl pointer-events-none" />
        <div className="absolute top-4 left-6 w-32 h-32 rounded-full border border-cyan-400/30 animate-pulse pointer-events-none" />

        <div className="relative flex flex-col md:flex-row items-start md:items-center gap-6">
          <div className="shrink-0">
            <BrandMark size={80} tone="dark" />
          </div>
          <div className="flex-1">
            <div className="inline-flex items-center gap-2 mb-3 px-2.5 py-1 rounded-full border border-emerald-500/40 bg-emerald-500/10">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-300" />
              <span className="text-[11px] uppercase tracking-widest font-bold text-emerald-300">
                Onboarding complete
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight">
              You\'re ready to inspect.
            </h2>
            <p className="mt-3 text-slate-300 text-base md:text-lg max-w-2xl leading-relaxed">
              Your workspace is configured for{' '}
              <span className="text-cyan-300 font-semibold">
                {summariseProfile(profile)}
              </span>. Pick where you want to start.
            </p>
          </div>
        </div>
      </div>

      {/* Three next actions */}
      <div className="mt-8 grid md:grid-cols-3 gap-4">
        <NextActionCard
          Icon={Map}
          tint="cyan"
          title="Open the live map"
          body="See every vessel in your fleet focus, with risk overlay and one-click vessel profiles."
          cta="Go to map"
          onClick={() => goAndComplete('/')}
        />
        <NextActionCard
          Icon={ClipboardCheck}
          tint="emerald"
          title="Browse open assignments"
          body="Inspections waiting for your sign-off, sorted by due date and severity."
          cta="See assignments"
          onClick={() => goAndComplete('/vetting/inspections')}
        />
        <NextActionCard
          Icon={Plus}
          tint="amber"
          title="Start a new inspection"
          body="Pick a vessel + regime, run the questionnaire, generate the report."
          cta="New inspection"
          onClick={() => goAndComplete('/vetting/inspections/new')}
        />
      </div>

      {/* Footer help cue */}
      <div className="mt-8 rounded-xl border border-slate-800 bg-slate-900/40 px-5 py-4 flex items-start gap-3">
        <HelpCircle className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />
        <div className="text-sm text-slate-300">
          <span className="font-semibold">Need a refresher?</span>{' '}
          Re-launch this tour anytime from the Help menu, or jump to specific
          articles via the Help drawer (the floating <span className="font-bold">?</span> at the bottom-right of every page).
        </div>
      </div>

      <div className="mt-8 flex justify-end">
        <button
          onClick={onBack}
          className="text-xs text-slate-500 hover:text-slate-300 transition-colors mr-4"
        >
          Back
        </button>
      </div>
    </StepShell>
  );
}

function NextActionCard({
  Icon,
  tint,
  title,
  body,
  cta,
  onClick,
}: {
  Icon: React.ComponentType<{ className?: string }>;
  tint: 'cyan' | 'emerald' | 'amber';
  title: string;
  body: string;
  cta: string;
  onClick: () => void;
}) {
  const tintCls =
    tint === 'cyan'
      ? { ring: 'hover:border-cyan-500/40', chip: 'bg-cyan-500/10 border-cyan-500/30',       icon: 'text-cyan-300',    btn: 'text-cyan-300 hover:text-cyan-200' }
      : tint === 'emerald'
      ? { ring: 'hover:border-emerald-500/40', chip: 'bg-emerald-500/10 border-emerald-500/30', icon: 'text-emerald-300', btn: 'text-emerald-300 hover:text-emerald-200' }
      : { ring: 'hover:border-amber-500/40', chip: 'bg-amber-500/10 border-amber-500/30',     icon: 'text-amber-300',   btn: 'text-amber-300 hover:text-amber-200' };

  return (
    <button
      onClick={onClick}
      className={`text-left rounded-xl bg-slate-900/60 border border-slate-800 p-5 transition-all duration-200 active:scale-[0.99] ${tintCls.ring}`}
    >
      <div className={`w-11 h-11 rounded-lg border flex items-center justify-center mb-3 ${tintCls.chip}`}>
        <Icon className={`w-5 h-5 ${tintCls.icon}`} />
      </div>
      <h3 className="text-base font-bold text-white mb-1">{title}</h3>
      <p className="text-sm text-slate-400 leading-relaxed mb-3.5">{body}</p>
      <span className={`inline-flex items-center gap-1 text-sm font-semibold ${tintCls.btn}`}>
        <span>{cta}</span>
        <ArrowRight className="w-3.5 h-3.5" />
      </span>
    </button>
  );
}

function summariseProfile(p: OnboardingProfile): string {
  const parts: string[] = [];
  if (p.regimes.length) parts.push(p.regimes.slice(0, 2).join(' + '));
  if (p.fleets.length)  parts.push(`${p.fleets.length} fleet${p.fleets.length > 1 ? 's' : ''}`);
  if (parts.length === 0) return 'your role';
  return parts.join(' · ');
}
